import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid
} from "recharts";
import {
  Euro,
  TrendingUp,
  TrendingDown,
  Receipt,
  Wallet,
  Plus,
  FileText,
  Upload,
  Download,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Loader2,
  ArrowUpRight,
  ArrowDownRight,
  PieChart as PieChartIcon,
  Settings,
  FolderPlus
} from "lucide-react";
import { format, subMonths, addMonths } from "date-fns";
import { it } from "date-fns/locale";

const COLORS = ['#8b5cf6', '#ec4899', '#6366f1', '#14b8a6', '#f59e0b', '#ef4444', '#3b82f6', '#10b981', '#6b7280'];

export default function BilancioMensile() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [meseCorrente, setMeseCorrente] = useState(format(new Date(), "yyyy-MM"));
  const [bilancio, setBilancio] = useState(null);
  // Movimenti unificati (spese + ordini fornitori) per il mese
  const [movimenti, setMovimenti] = useState([]);
  const [filtroPagato, setFiltroPagato] = useState("tutti"); // tutti | da_pagare | pagati
  // Macrocategorie e Categorie
  const [macrocategorie, setMacrocategorie] = useState([]);
  const [categorie, setCategorie] = useState([]);
  const [spese, setSpese] = useState([]);
  
  // Dialogs
  const [spesaDialogOpen, setSpesaDialogOpen] = useState(false);
  const [categorieDialogOpen, setCategorieDialogOpen] = useState(false);
  const [fatturaDialogOpen, setFatturaDialogOpen] = useState(false);
  
  // Form spesa
  const [spesaForm, setSpesaForm] = useState({
    macrocategoria_id: "",
    categoria_id: "",
    descrizione: "",
    importo: "",
    data_spesa: format(new Date(), "yyyy-MM-dd"),
    data_scadenza: "",
    pagato: false,
    note: "",
    fornitore_id: ""  // Opzionale: se selezionato, la spesa appare nella sezione Fornitori
  });
  // Lista fornitori per il select del form Registra Spesa
  const [fornitoriList, setFornitoriList] = useState([]);
  
  // Form nuova categoria
  const [nuovaMacro, setNuovaMacro] = useState({ nome: "", colore: "#6b7280" });
  const [nuovaCat, setNuovaCat] = useState({ macrocategoria_id: "", nome: "" });
  
  // Quick-add popovers nel form Registra Spesa
  const [quickMacroOpen, setQuickMacroOpen] = useState(false);
  const [quickCatOpen, setQuickCatOpen] = useState(false);
  const [quickMacroNome, setQuickMacroNome] = useState("");
  const [quickMacroColore, setQuickMacroColore] = useState("#6b7280");
  const [quickCatNome, setQuickCatNome] = useState("");
  
  // Fattura upload
  const [fatturaFile, setFatturaFile] = useState(null);
  const [fatturaUploading, setFatturaUploading] = useState(false);
  const [fatturaData, setFatturaData] = useState(null);

  useEffect(() => {
    fetchData();
  }, [meseCorrente]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [bilRes, macroRes, catRes, speseRes, movRes, fornRes] = await Promise.all([
        axios.get(`${API}/spese/bilancio-mensile?mese=${meseCorrente}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/spese/macrocategorie`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/spese/categorie`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/spese?mese=${meseCorrente}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/bilancio/movimenti?mese=${meseCorrente}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/fornitori`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setBilancio(bilRes.data);
      setMacrocategorie(macroRes.data);
      setCategorie(catRes.data);
      setSpese(speseRes.data);
      setMovimenti(movRes.data.movimenti || []);
      setFornitoriList(fornRes.data || []);
      
      // Se non ci sono categorie, inizializzale
      if (macroRes.data.length === 0) {
        await initCategorie();
      }
    } catch (error) {
      // Se errore 404, probabilmente le categorie non esistono
      if (error.response?.status === 404 || macrocategorie.length === 0) {
        await initCategorie();
      }
    } finally {
      setLoading(false);
    }
  };

  const initCategorie = async () => {
    try {
      await axios.post(`${API}/spese/init-categorie`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Ricarica
      const [macroRes, catRes] = await Promise.all([
        axios.get(`${API}/spese/macrocategorie`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/spese/categorie`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setMacrocategorie(macroRes.data);
      setCategorie(catRes.data);
    } catch (error) {
      console.error("Errore caricamento categorie:", error);
    }
  };

  // Toggle pagato/non pagato per movimento (spesa o ordine fornitore)
  const toggleMovimentoPagato = async (movimento) => {
    try {
      if (movimento.tipo === "spesa") {
        await axios.post(`${API}/spese/${movimento.id}/toggle-pagato`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else if (movimento.tipo === "fornitore") {
        if (!movimento.pagato) {
          await axios.post(`${API}/ordini-fornitori/${movimento.id}/segna-pagato`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } else {
          // riapri pagamento -> imposta stato_pagamento=da_pagare e rimuove data_pagamento
          await axios.put(`${API}/ordini-fornitori/${movimento.id}`, {
            stato_pagamento: "da_pagare",
            data_pagamento: null
          }, { headers: { Authorization: `Bearer ${token}` } });
        }
      }
      toast.success(movimento.pagato ? "Riaperto" : "Segnato come pagato");
      fetchData();
    } catch (err) {
      toast.error("Errore aggiornamento stato pagamento");
    }
  };


  const handleMesePrev = () => {
    const prevMese = format(subMonths(new Date(meseCorrente + "-01"), 1), "yyyy-MM");
    setMeseCorrente(prevMese);
  };

  const handleMeseNext = () => {
    const nextMese = format(addMonths(new Date(meseCorrente + "-01"), 1), "yyyy-MM");
    setMeseCorrente(nextMese);
  };

  const handleSalvaSpesa = async () => {
    if (!spesaForm.macrocategoria_id || !spesaForm.importo) {
      toast.error("Compila macrocategoria e importo");
      return;
    }
    
    try {
      await axios.post(`${API}/spese`, {
        ...spesaForm,
        importo: parseFloat(spesaForm.importo),
        data_scadenza: spesaForm.data_scadenza || null,
        pagato: Boolean(spesaForm.pagato),
        fornitore_id: spesaForm.fornitore_id || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Spesa registrata!");
      setSpesaDialogOpen(false);
      setSpesaForm({
        macrocategoria_id: "",
        categoria_id: "",
        descrizione: "",
        importo: "",
        data_spesa: format(new Date(), "yyyy-MM-dd"),
        data_scadenza: "",
        pagato: false,
        note: "",
        fornitore_id: ""
      });
      fetchData();
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  const handleEliminaSpesa = async (spesaId) => {
    try {
      await axios.delete(`${API}/spese/${spesaId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Spesa eliminata");
      fetchData();
    } catch (error) {
      toast.error("Errore eliminazione");
    }
  };

  const handleCreaMacrocategoria = async () => {
    if (!nuovaMacro.nome) return;
    try {
      await axios.post(`${API}/spese/macrocategorie`, nuovaMacro, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Macrocategoria creata!");
      setNuovaMacro({ nome: "", colore: "#6b7280" });
      fetchData();
    } catch (error) {
      toast.error("Errore creazione");
    }
  };

  const handleCreaCategoria = async () => {
    if (!nuovaCat.macrocategoria_id || !nuovaCat.nome) return;
    try {
      await axios.post(`${API}/spese/categorie`, nuovaCat, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Categoria creata!");
      setNuovaCat({ macrocategoria_id: "", nome: "" });
      fetchData();
    } catch (error) {
      toast.error("Errore creazione");
    }
  };

  // Quick-add Macrocategoria dal form Registra Spesa
  const handleQuickAddMacro = async () => {
    const nome = quickMacroNome.trim();
    if (!nome) {
      toast.error("Inserisci il nome");
      return;
    }
    try {
      const res = await axios.post(`${API}/spese/macrocategorie`, {
        nome,
        colore: quickMacroColore
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Macrocategoria creata!");
      // Refresh e auto-seleziona la nuova macro nel form
      const macroRes = await axios.get(`${API}/spese/macrocategorie`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMacrocategorie(macroRes.data);
      const newId = res.data?.id;
      if (newId) {
        setSpesaForm(prev => ({...prev, macrocategoria_id: newId, categoria_id: ""}));
      }
      setQuickMacroNome("");
      setQuickMacroColore("#6b7280");
      setQuickMacroOpen(false);
    } catch (error) {
      toast.error("Errore creazione macrocategoria");
    }
  };

  // Quick-add Categoria dal form Registra Spesa (eredita la macro selezionata)
  const handleQuickAddCat = async () => {
    const nome = quickCatNome.trim();
    if (!spesaForm.macrocategoria_id) {
      toast.error("Seleziona prima una macrocategoria");
      return;
    }
    if (!nome) {
      toast.error("Inserisci il nome");
      return;
    }
    try {
      const res = await axios.post(`${API}/spese/categorie`, {
        macrocategoria_id: spesaForm.macrocategoria_id,
        nome
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Categoria creata!");
      const catRes = await axios.get(`${API}/spese/categorie`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategorie(catRes.data);
      const newId = res.data?.id;
      if (newId) {
        setSpesaForm(prev => ({...prev, categoria_id: newId}));
      }
      setQuickCatNome("");
      setQuickCatOpen(false);
    } catch (error) {
      toast.error("Errore creazione categoria");
    }
  };

  const handleUploadFattura = async () => {
    if (!fatturaFile) return;
    
    setFatturaUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", fatturaFile);
      
      const response = await axios.post(`${API}/fatture/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      
      setFatturaData(response.data);
      toast.success("Fattura caricata! Verifica i dati estratti.");
    } catch (error) {
      toast.error("Errore caricamento fattura");
    } finally {
      setFatturaUploading(false);
    }
  };

  const handleRegistraSpesaDaFattura = async () => {
    if (!fatturaData?.id) return;
    
    try {
      await axios.post(`${API}/fatture/${fatturaData.id}/registra-spesa`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Spesa registrata dalla fattura!");
      setFatturaDialogOpen(false);
      setFatturaData(null);
      setFatturaFile(null);
      fetchData();
    } catch (error) {
      toast.error("Errore registrazione spesa");
    }
  };

  const categorieFiltered = categorie.filter(
    c => c.macrocategoria_id === spesaForm.macrocategoria_id
  );

  const meseLabel = format(new Date(meseCorrente + "-01"), "MMMM yyyy", { locale: it });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header con navigazione mese */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={handleMesePrev}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <h2 className="text-xl font-bold capitalize">{meseLabel}</h2>
          <Button variant="outline" size="icon" onClick={handleMeseNext}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setCategorieDialogOpen(true)}>
            <Settings className="w-4 h-4 mr-2" />
            Categorie
          </Button>
          <Button variant="outline" onClick={() => setFatturaDialogOpen(true)}>
            <Upload className="w-4 h-4 mr-2" />
            Carica Fattura
          </Button>
          <Button onClick={() => setSpesaDialogOpen(true)} className="bg-red-600 hover:bg-red-700">
            <Plus className="w-4 h-4 mr-2" />
            Registra Spesa
          </Button>
        </div>
      </div>

      {/* Cards Riepilogo */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Incassi Netti</p>
                <p className="text-3xl font-bold mt-1">
                  €{bilancio?.incassi?.netto?.toLocaleString('it-IT') || 0}
                </p>
                <p className="text-green-200 text-xs mt-1">
                  {bilancio?.incassi?.num_transazioni || 0} transazioni
                </p>
              </div>
              <ArrowUpRight className="w-10 h-10 text-green-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm">Spese Totali</p>
                <p className="text-3xl font-bold mt-1">
                  €{bilancio?.spese?.totale?.toLocaleString('it-IT') || 0}
                </p>
                <p className="text-red-200 text-xs mt-1">
                  {bilancio?.spese?.num_spese || 0} voci registrate
                </p>
              </div>
              <ArrowDownRight className="w-10 h-10 text-red-200" />
            </div>
          </CardContent>
        </Card>

        <Card className={`bg-gradient-to-br ${
          (bilancio?.bilancio?.utile_lordo || 0) >= 0 
            ? 'from-blue-500 to-blue-600' 
            : 'from-orange-500 to-orange-600'
        } text-white`}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Utile Lordo</p>
                <p className="text-3xl font-bold mt-1">
                  €{bilancio?.bilancio?.utile_lordo?.toLocaleString('it-IT') || 0}
                </p>
                <p className="text-blue-200 text-xs mt-1">
                  Margine: {bilancio?.bilancio?.margine_percentuale || 0}%
                </p>
              </div>
              {(bilancio?.bilancio?.utile_lordo || 0) >= 0 ? (
                <TrendingUp className="w-10 h-10 text-blue-200" />
              ) : (
                <TrendingDown className="w-10 h-10 text-orange-200" />
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">IVA Spese</p>
                <p className="text-3xl font-bold mt-1">
                  €{bilancio?.spese?.iva?.toLocaleString('it-IT') || 0}
                </p>
                <p className="text-purple-200 text-xs mt-1">
                  Imponibile: €{bilancio?.spese?.imponibile?.toLocaleString('it-IT') || 0}
                </p>
              </div>
              <Receipt className="w-10 h-10 text-purple-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pie Chart Entrate per Categoria + Spese per Categoria */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChartIcon className="w-5 h-5 text-emerald-500" />
              Entrate per Categoria
            </CardTitle>
            <p className="text-xs text-slate-400 mt-1">
              Da servizi e rivendita prodotti — totale netto €{(bilancio?.incassi?.netto || 0).toFixed(2)}
            </p>
          </CardHeader>
          <CardContent>
            {bilancio?.incassi?.per_categoria?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={bilancio.incassi.per_categoria}
                    dataKey="valore"
                    nameKey="nome"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={({ nome, valore }) => `${nome}: €${valore}`}
                  >
                    {bilancio.incassi.per_categoria.map((entry, index) => (
                      <Cell key={`cell-in-${index}`} fill={['#10b981', '#14b8a6', '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1', '#8b5cf6'][index % 7]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => `€${value.toLocaleString('it-IT')}`}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-slate-400">
                Nessuna entrata registrata questo mese
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChartIcon className="w-5 h-5 text-rose-500" />
              Spese Extra per Categoria
            </CardTitle>
            <p className="text-xs text-slate-400 mt-1">
              Solo spese extra €{(bilancio?.spese?.spese_extra || 0).toFixed(2)} (i fornitori sono nella sezione dedicata sotto)
            </p>
          </CardHeader>
          <CardContent>
            {bilancio?.spese?.per_categoria?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={bilancio.spese.per_categoria}
                    dataKey="valore"
                    nameKey="nome"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={({ nome, valore }) => `${nome}: €${valore}`}
                  >
                    {bilancio.spese.per_categoria.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => `€${value.toLocaleString('it-IT')}`}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-slate-400">
                Nessuna spesa registrata questo mese
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* NUOVA SEZIONE FORNITORI — pie chart + lista aggregata per fornitore */}
      <Card className="border-blue-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <PieChartIcon className="w-5 h-5 text-blue-600" />
              Fornitori del Mese
            </CardTitle>
            <div className="text-right">
              <p className="text-xs text-slate-400 uppercase">Totale ordini</p>
              <p className="text-xl font-bold text-blue-700">
                €{(bilancio?.fornitori?.totale || 0).toFixed(2)}
              </p>
              <p className="text-[10px] text-slate-400">
                {bilancio?.fornitori?.num_ordini || 0} ordini
              </p>
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Sincronizzati automaticamente dagli ordini fornitori. Sezione dedicata: gli importi NON sono inclusi nelle macrocategorie spese.
          </p>
        </CardHeader>
        <CardContent>
          {bilancio?.fornitori?.per_fornitore?.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Pie chart per fornitore */}
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={bilancio.fornitori.per_fornitore}
                    dataKey="valore"
                    nameKey="nome"
                    cx="50%"
                    cy="50%"
                    outerRadius={95}
                    label={({ nome, valore }) => `${nome}: €${valore}`}
                  >
                    {bilancio.fornitori.per_fornitore.map((entry, index) => (
                      <Cell key={`forn-${index}`} fill={['#2563eb', '#0ea5e9', '#06b6d4', '#0d9488', '#7c3aed', '#a855f7', '#d946ef'][index % 7]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `€${value.toLocaleString('it-IT')}`} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>

              {/* Lista fornitori con pagato/da pagare */}
              <div className="space-y-2 max-h-[280px] overflow-y-auto" data-testid="lista-fornitori-bilancio">
                {bilancio.fornitori.per_fornitore.map((f, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-blue-50 border border-blue-100 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-800 truncate">{f.nome}</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {f.conteggio} {f.conteggio === 1 ? 'ordine' : 'ordini'}
                        {f.da_pagare > 0 && <span className="text-orange-600 ml-2">• Da pagare: €{f.da_pagare.toFixed(2)}</span>}
                      </p>
                    </div>
                    <div className="text-right shrink-0 ml-2">
                      <p className="font-bold text-blue-700">€{f.valore.toFixed(2)}</p>
                      {f.pagato > 0 && (
                        <p className="text-[10px] text-emerald-600">✓ Pagato: €{f.pagato.toFixed(2)}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              Nessun ordine fornitore registrato questo mese
            </div>
          )}
        </CardContent>
      </Card>



      {/* Lista Movimenti del Mese — Spese + Ordini Fornitori unificati */}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-red-500" />
              Movimenti del Mese ({movimenti.length})
            </CardTitle>
            {/* Filtro pagato/da pagare */}
            <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
              <button
                onClick={() => setFiltroPagato("tutti")}
                className={`text-xs px-3 py-1.5 rounded-md transition-all ${filtroPagato === "tutti" ? 'bg-white shadow font-semibold text-slate-800' : 'text-slate-500'}`}
                data-testid="filtro-mov-tutti"
              >
                Tutti
              </button>
              <button
                onClick={() => setFiltroPagato("da_pagare")}
                className={`text-xs px-3 py-1.5 rounded-md transition-all ${filtroPagato === "da_pagare" ? 'bg-white shadow font-semibold text-orange-700' : 'text-slate-500'}`}
                data-testid="filtro-mov-da-pagare"
              >
                Da Pagare
              </button>
              <button
                onClick={() => setFiltroPagato("pagati")}
                className={`text-xs px-3 py-1.5 rounded-md transition-all ${filtroPagato === "pagati" ? 'bg-white shadow font-semibold text-emerald-700' : 'text-slate-500'}`}
                data-testid="filtro-mov-pagati"
              >
                Pagati
              </button>
            </div>
          </div>
          {/* Mini KPI */}
          <div className="grid grid-cols-3 gap-2 mt-3">
            <div className="bg-slate-50 rounded-lg p-2">
              <p className="text-[10px] text-slate-500 uppercase">Totale Mese</p>
              <p className="text-lg font-bold text-slate-700">
                €{movimenti.reduce((s, m) => s + m.importo, 0).toFixed(2)}
              </p>
            </div>
            <div className="bg-emerald-50 rounded-lg p-2">
              <p className="text-[10px] text-emerald-700 uppercase">Pagato</p>
              <p className="text-lg font-bold text-emerald-700">
                €{movimenti.filter(m => m.pagato).reduce((s, m) => s + m.importo, 0).toFixed(2)}
              </p>
            </div>
            <div className="bg-orange-50 rounded-lg p-2">
              <p className="text-[10px] text-orange-700 uppercase">Da Pagare</p>
              <p className="text-lg font-bold text-orange-700">
                €{movimenti.filter(m => !m.pagato).reduce((s, m) => s + m.importo, 0).toFixed(2)}
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 max-h-[450px] overflow-y-auto">
            {(() => {
              const filtered = movimenti.filter(m => {
                if (filtroPagato === "da_pagare") return !m.pagato;
                if (filtroPagato === "pagati") return m.pagato;
                return true;
              });
              if (filtered.length === 0) {
                return (
                  <div className="text-center py-8 text-slate-400">
                    Nessun movimento {filtroPagato !== "tutti" ? `(${filtroPagato})` : ""}
                  </div>
                );
              }
              return filtered.map(mov => {
                const isOverdue = !mov.pagato && mov.data_scadenza && mov.data_scadenza < new Date().toISOString().slice(0,10);
                return (
                  <div
                    key={`${mov.tipo}-${mov.id}`}
                    className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                      mov.pagato ? 'bg-emerald-50 border-emerald-100' : isOverdue ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-100'
                    }`}
                    data-testid={`movimento-${mov.tipo}-${mov.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`inline-block text-[10px] px-2 py-0.5 rounded-full font-semibold ${mov.tipo === "fornitore" ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                          {mov.tipo === "fornitore" ? "FORNITORE" : "EXTRA"}
                        </span>
                        <p className="font-medium text-slate-800 truncate">
                          {mov.descrizione}
                        </p>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {mov.macrocategoria_nome}{mov.categoria_nome ? ` → ${mov.categoria_nome}` : ""} • {mov.data}
                        {mov.data_scadenza && (
                          <span className={isOverdue ? "text-red-600 font-bold ml-2" : "ml-2"}>
                            • Scad: {mov.data_scadenza}{isOverdue ? " ⚠ SCADUTA" : ""}
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 ml-2">
                      <span className={`font-bold ${mov.pagato ? 'text-emerald-700' : 'text-red-600'}`}>
                        -€{mov.importo.toFixed(2)}
                      </span>
                      <Button
                        variant={mov.pagato ? "outline" : "default"}
                        size="sm"
                        onClick={() => toggleMovimentoPagato(mov)}
                        className={mov.pagato ? "h-7 text-[11px] text-emerald-700 border-emerald-300" : "h-7 text-[11px] bg-orange-600 hover:bg-orange-700 text-white"}
                        data-testid={`toggle-pagato-${mov.id}`}
                      >
                        {mov.pagato ? "✓ Pagato" : "Segna Pagato"}
                      </Button>
                      {mov.tipo === "spesa" && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEliminaSpesa(mov.id)}
                          className="text-red-400 hover:text-red-600 hover:bg-red-50 h-7 w-7"
                          data-testid={`elimina-spesa-${mov.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </CardContent>
      </Card>

      {/* Dialog Registra Spesa */}
      <Dialog open={spesaDialogOpen} onOpenChange={setSpesaDialogOpen}>
        <DialogContent className="!max-w-md w-[95vw] max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-red-500" />
              Registra Spesa
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Macrocategoria *</Label>
              <div className="flex gap-2">
                <Select 
                  value={spesaForm.macrocategoria_id} 
                  onValueChange={(v) => setSpesaForm({...spesaForm, macrocategoria_id: v, categoria_id: ""})}
                >
                  <SelectTrigger className="flex-1" data-testid="select-macrocategoria-spesa">
                    <SelectValue placeholder="Seleziona macrocategoria..." />
                  </SelectTrigger>
                  <SelectContent>
                    {macrocategorie.map(m => (
                      <SelectItem key={m.id} value={m.id}>{m.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Popover open={quickMacroOpen} onOpenChange={setQuickMacroOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      className="shrink-0 border-blue-300 text-blue-600 hover:bg-blue-50"
                      title="Crea nuova macrocategoria"
                      data-testid="btn-quick-add-macro"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 p-3" side="bottom" align="end">
                    <div className="space-y-2">
                      <Label className="text-xs font-semibold text-blue-700">Nuova Macrocategoria</Label>
                      <Input
                        value={quickMacroNome}
                        onChange={(e) => setQuickMacroNome(e.target.value)}
                        placeholder="Es: Ordini Tecnico"
                        autoFocus
                        data-testid="input-quick-macro-nome"
                      />
                      <div className="flex items-center gap-2">
                        <Label className="text-xs text-slate-500">Colore:</Label>
                        <input
                          type="color"
                          value={quickMacroColore}
                          onChange={(e) => setQuickMacroColore(e.target.value)}
                          className="w-8 h-8 rounded cursor-pointer border"
                        />
                      </div>
                      <div className="flex gap-2 pt-1">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => { setQuickMacroOpen(false); setQuickMacroNome(""); }}
                        >
                          Annulla
                        </Button>
                        <Button
                          size="sm"
                          className="flex-1 bg-blue-600 hover:bg-blue-700"
                          onClick={handleQuickAddMacro}
                          data-testid="btn-save-quick-macro"
                        >
                          Crea
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Categoria</Label>
              <div className="flex gap-2">
                <Select 
                  value={spesaForm.categoria_id} 
                  onValueChange={(v) => setSpesaForm({...spesaForm, categoria_id: v})}
                  disabled={!spesaForm.macrocategoria_id}
                >
                  <SelectTrigger className="flex-1" data-testid="select-categoria-spesa">
                    <SelectValue placeholder="Seleziona categoria..." />
                  </SelectTrigger>
                  <SelectContent>
                    {categorieFiltered.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Popover open={quickCatOpen} onOpenChange={setQuickCatOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      disabled={!spesaForm.macrocategoria_id}
                      className="shrink-0 border-green-300 text-green-600 hover:bg-green-50 disabled:opacity-40"
                      title={spesaForm.macrocategoria_id ? "Crea nuova categoria" : "Seleziona prima una macrocategoria"}
                      data-testid="btn-quick-add-cat"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 p-3" side="bottom" align="end">
                    <div className="space-y-2">
                      <Label className="text-xs font-semibold text-green-700">Nuova Categoria</Label>
                      <p className="text-[10px] text-slate-500">
                        In: <span className="font-medium">
                          {macrocategorie.find(m => m.id === spesaForm.macrocategoria_id)?.nome || "—"}
                        </span>
                      </p>
                      <Input
                        value={quickCatNome}
                        onChange={(e) => setQuickCatNome(e.target.value)}
                        placeholder="Es: LPS, MLD, TIM..."
                        autoFocus
                        data-testid="input-quick-cat-nome"
                      />
                      <div className="flex gap-2 pt-1">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => { setQuickCatOpen(false); setQuickCatNome(""); }}
                        >
                          Annulla
                        </Button>
                        <Button
                          size="sm"
                          className="flex-1 bg-green-600 hover:bg-green-700"
                          onClick={handleQuickAddCat}
                          data-testid="btn-save-quick-cat"
                        >
                          Crea
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Importo (€) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={spesaForm.importo}
                  onChange={(e) => setSpesaForm({...spesaForm, importo: e.target.value})}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label>Data</Label>
                <Input
                  type="date"
                  value={spesaForm.data_spesa}
                  onChange={(e) => setSpesaForm({...spesaForm, data_spesa: e.target.value})}
                />
              </div>
            </div>

            {/* Fornitore (opzionale) - se selezionato, la spesa appare nella sezione Fornitori del Bilancio */}
            <div className="space-y-2">
              <Label>Fornitore (opzionale)</Label>
              <Select
                value={spesaForm.fornitore_id || "_none"}
                onValueChange={(val) => setSpesaForm({...spesaForm, fornitore_id: val === "_none" ? "" : val})}
              >
                <SelectTrigger data-testid="select-fornitore-spesa">
                  <SelectValue placeholder="Nessun fornitore - spesa extra" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Nessun fornitore (spesa extra)</SelectItem>
                  {fornitoriList.map(f => (
                    <SelectItem key={f.id} value={f.id}>{f.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-slate-400">
                Se selezioni un fornitore, la spesa verrà aggregata automaticamente nella sezione &quot;Fornitori del Mese&quot; del Bilancio.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Descrizione</Label>
              <Input
                value={spesaForm.descrizione}
                onChange={(e) => setSpesaForm({...spesaForm, descrizione: e.target.value})}
                placeholder="Es: Fattura L'Oréal marzo"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Data Scadenza (opzionale)</Label>
                <Input
                  type="date"
                  value={spesaForm.data_scadenza}
                  onChange={(e) => setSpesaForm({...spesaForm, data_scadenza: e.target.value})}
                  data-testid="input-data-scadenza-spesa"
                />
                <p className="text-[10px] text-slate-400">Se vuota = scadenza immediata</p>
              </div>
              <div className="space-y-2">
                <Label>Stato Pagamento</Label>
                <div className="flex items-center gap-2 h-10">
                  <input
                    type="checkbox"
                    id="spesa-pagato"
                    checked={spesaForm.pagato}
                    onChange={(e) => setSpesaForm({...spesaForm, pagato: e.target.checked})}
                    className="w-5 h-5"
                    data-testid="checkbox-pagato-spesa"
                  />
                  <label htmlFor="spesa-pagato" className="text-sm">
                    {spesaForm.pagato ? "✓ Già pagato" : "Da pagare"}
                  </label>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea
                value={spesaForm.note}
                onChange={(e) => setSpesaForm({...spesaForm, note: e.target.value})}
                placeholder="Note aggiuntive..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSpesaDialogOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleSalvaSpesa} className="bg-red-600 hover:bg-red-700">
              Registra Spesa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Gestione Categorie */}
      <Dialog open={categorieDialogOpen} onOpenChange={setCategorieDialogOpen}>
        <DialogContent className="!max-w-lg w-[95vw] max-h-[90vh] sm:max-h-[85vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Settings className="w-5 h-5 text-blue-500" />
              Gestione Categorie Spese
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 sm:space-y-6 py-2 sm:py-4">
            {/* Nuova Macrocategoria */}
            <div className="p-3 sm:p-4 bg-blue-50 rounded-lg space-y-2 sm:space-y-3">
              <Label className="font-semibold text-sm">Nuova Macrocategoria</Label>
              <div className="flex flex-wrap gap-2 items-center">
                <Input
                  value={nuovaMacro.nome}
                  onChange={(e) => setNuovaMacro({...nuovaMacro, nome: e.target.value})}
                  placeholder="Es: Ordini Tecnico"
                  className="flex-1 min-w-[150px]"
                />
                <input
                  type="color"
                  value={nuovaMacro.colore}
                  onChange={(e) => setNuovaMacro({...nuovaMacro, colore: e.target.value})}
                  className="w-10 h-10 rounded cursor-pointer shrink-0"
                />
                <Button onClick={handleCreaMacrocategoria} size="icon" className="shrink-0">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Nuova Categoria */}
            <div className="p-3 sm:p-4 bg-green-50 rounded-lg space-y-2 sm:space-y-3">
              <Label className="font-semibold text-sm">Nuova Categoria</Label>
              <div className="flex flex-col sm:flex-row gap-2">
                <Select 
                  value={nuovaCat.macrocategoria_id} 
                  onValueChange={(v) => setNuovaCat({...nuovaCat, macrocategoria_id: v})}
                >
                  <SelectTrigger className="w-full sm:w-40">
                    <SelectValue placeholder="Macro..." />
                  </SelectTrigger>
                  <SelectContent>
                    {macrocategorie.map(m => (
                      <SelectItem key={m.id} value={m.id}>{m.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex gap-2 flex-1">
                  <Input
                    value={nuovaCat.nome}
                    onChange={(e) => setNuovaCat({...nuovaCat, nome: e.target.value})}
                    placeholder="Es: LPS, MLD, TIM..."
                    className="flex-1"
                  />
                  <Button onClick={handleCreaCategoria} size="icon" variant="outline" className="shrink-0">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Lista Categorie Esistenti con rinomina/elimina */}
            <div className="space-y-3">
              <Label className="font-semibold">Categorie Esistenti</Label>
              {macrocategorie.map(macro => (
                <div key={macro.id} className="border rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: macro.colore }}
                    />
                    <Input
                      value={macro.nome}
                      onChange={(e) => {
                        const next = macrocategorie.map(m => m.id === macro.id ? {...m, nome: e.target.value} : m);
                        setMacrocategorie(next);
                      }}
                      onBlur={async (e) => {
                        const newName = e.target.value.trim();
                        if (!newName || newName === macro.nome) return;
                        try {
                          await axios.put(`${API}/spese/macrocategorie/${macro.id}`, { nome: newName }, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          toast.success("Nome aggiornato");
                          fetchData();
                        } catch {
                          toast.error("Errore aggiornamento");
                        }
                      }}
                      className="h-7 font-medium border-transparent hover:border-slate-200 focus:border-blue-300"
                      data-testid={`rinomina-macro-${macro.id}`}
                    />
                    <span className="text-xs text-slate-400 shrink-0">
                      ({categorie.filter(c => c.macrocategoria_id === macro.id).length})
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-red-400 hover:text-red-600 hover:bg-red-50 shrink-0"
                      onClick={async () => {
                        const num = categorie.filter(c => c.macrocategoria_id === macro.id).length;
                        if (num > 0) {
                          toast.error(`Rimuovi prima le ${num} categorie collegate`);
                          return;
                        }
                        if (!window.confirm(`Eliminare la macrocategoria "${macro.nome}"?`)) return;
                        try {
                          await axios.delete(`${API}/spese/macrocategorie/${macro.id}`, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          toast.success("Macrocategoria eliminata");
                          fetchData();
                        } catch {
                          toast.error("Errore eliminazione");
                        }
                      }}
                      data-testid={`elimina-macro-${macro.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-1 ml-5">
                    {categorie
                      .filter(c => c.macrocategoria_id === macro.id)
                      .map(cat => (
                        <span 
                          key={cat.id}
                          className="px-2 py-0.5 bg-slate-100 rounded text-xs text-slate-600"
                        >
                          {cat.nome}
                        </span>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Carica Fattura */}
      <Dialog open={fatturaDialogOpen} onOpenChange={(open) => {
        setFatturaDialogOpen(open);
        if (!open) {
          setFatturaFile(null);
          setFatturaData(null);
        }
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-purple-500" />
              Carica Fattura Fornitore
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {!fatturaData ? (
              <>
                <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => setFatturaFile(e.target.files[0])}
                    className="hidden"
                    id="fattura-upload"
                  />
                  <label htmlFor="fattura-upload" className="cursor-pointer">
                    <Upload className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-600 font-medium">
                      {fatturaFile ? fatturaFile.name : "Clicca per caricare PDF"}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      L&apos;AI estrarrà automaticamente i dati
                    </p>
                  </label>
                </div>

                <Button 
                  onClick={handleUploadFattura} 
                  disabled={!fatturaFile || fatturaUploading}
                  className="w-full"
                >
                  {fatturaUploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Analisi in corso...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Carica e Analizza
                    </>
                  )}
                </Button>
              </>
            ) : (
              <>
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-green-700 font-medium mb-2">Dati Estratti:</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>Fornitore: <strong>{fatturaData.fornitore_nome || 'N/D'}</strong></div>
                    <div>N° Fattura: <strong>{fatturaData.numero_fattura || 'N/D'}</strong></div>
                    <div>Data: <strong>{fatturaData.data_fattura || 'N/D'}</strong></div>
                    <div>Totale: <strong className="text-red-600">€{fatturaData.totale}</strong></div>
                    <div>IVA: <strong>€{fatturaData.iva}</strong></div>
                    <div>Imponibile: <strong>€{fatturaData.imponibile}</strong></div>
                  </div>
                </div>

                {fatturaData.prodotti_estratti?.length > 0 && (
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-blue-700 font-medium mb-2">
                      Prodotti Trovati ({fatturaData.prodotti_estratti.length}):
                    </p>
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {fatturaData.prodotti_estratti.map((p, i) => (
                        <div key={`${p.nome}-${p.quantita}-${i}`} className="text-xs flex justify-between">
                          <span>{p.nome}</span>
                          <span>x{p.quantita} = €{p.totale}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => { setFatturaData(null); setFatturaFile(null); }}
                    className="flex-1"
                  >
                    Carica Altra
                  </Button>
                  <Button 
                    onClick={handleRegistraSpesaDaFattura}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    Registra come Spesa
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
